<template>
  <div class="stats-page">
    <h2>📊 Статистика занятий</h2>

    <div v-if="sessions.length === 0" class="empty">
      Пока нет завершённых занятий
    </div>

    <template v-else>
      <div class="tabs">
        <button :class="['tab', { active: tab === 'syllables' }]" @click="switchTab('syllables')">Слоги</button>
        <button :class="['tab', { active: tab === 'words' }]" @click="switchTab('words')">Слова</button>
      </div>

      <div class="chart-box">
        <canvas ref="chartRef"></canvas>
      </div>

      <div class="summary">
        <div class="sum-card">
          <div class="sum-val">{{ currentData.totalSessions }}</div>
          <div class="sum-label">Занятий</div>
        </div>
        <div class="sum-card">
          <div class="sum-val">{{ currentData.totalWords }}</div>
          <div class="sum-label">Пройдено</div>
        </div>
        <div class="sum-card">
          <div class="sum-val">{{ currentData.totalMinutes }}м</div>
          <div class="sum-label">Время</div>
        </div>
        <div class="sum-card">
          <div class="sum-val">{{ currentData.avgSuccess }}%</div>
          <div class="sum-label">Точность</div>
        </div>
      </div>

      <div class="session-list">
        <div v-for="(s, i) in currentData.recent" :key="i" class="session-row">
          <div class="session-date">{{ formatDate(s.startedAt) }}</div>
          <div class="session-result">
            <span class="ok">{{ s.correct }}</span>/<span class="err">{{ s.wrong }}</span>
          </div>
          <div class="session-time">{{ formatDuration(s.duration) }}</div>
          <div class="session-avg" v-if="s.avgTime">{{ s.avgTime }}с</div>
        </div>
      </div>
    </template>

    <div class="back">
      <router-link to="/">← На главную</router-link>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { Chart, registerables } from 'chart.js'
import { useSessionStats } from '../composables/useSessionStats.js'

Chart.register(...registerables)

const { sessions, getByDay, getRecent } = useSessionStats()

const tab = ref('syllables')
const chartRef = ref(null)
let chart = null

function filtered(type) {
  const r = getRecent(14).filter(s => s.type === type)
  const total = r.reduce((s, x) => s + x.total, 0)
  const correct = r.reduce((s, x) => s + x.correct, 0)
  return {
    totalSessions: r.length,
    totalWords: total,
    totalMinutes: Math.round(r.reduce((s, x) => s + x.duration, 0) / 60),
    avgSuccess: total ? Math.round(correct / total * 100) : 0,
    recent: [...r].reverse().slice(0, 50),
  }
}

const syllablesData = computed(() => filtered('syllables'))
const wordsData = computed(() => filtered('words'))
const currentData = computed(() => tab.value === 'syllables' ? syllablesData.value : wordsData.value)

function buildChart() {
  if (chart) { chart.destroy(); chart = null }
  if (!chartRef.value) return

  const r = getRecent(14)
  const byType = r.filter(s => s.type === tab.value)

  const map = {}
  for (const s of byType) {
    const d = new Date(s.startedAt)
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    if (!map[key]) map[key] = { total: 0, correct: 0 }
    map[key].total += s.total
    map[key].correct += s.correct
  }

  const labels = []
  const totals = []
  const corrects = []
  for (let i = 13; i >= 0; i--) {
    const d = new Date(Date.now() - i * 24 * 60 * 60 * 1000)
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    labels.push(`${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`)
    const e = map[key] || { total: 0, correct: 0 }
    totals.push(e.total)
    corrects.push(e.correct)
  }

  const color = tab.value === 'syllables' ? '#42b883' : '#e74c3c'
  const colorBg = tab.value === 'syllables' ? '#42b88366' : '#e74c3c66'

  chart = new Chart(chartRef.value.getContext('2d'), {
    type: 'bar',
    data: {
      labels,
      datasets: [
        {
          label: 'Пройдено',
          data: totals,
          backgroundColor: colorBg,
          borderColor: color,
          borderWidth: 1,
          borderRadius: 4,
        },
        {
          label: 'Правильно',
          data: corrects,
          backgroundColor: '#2196f366',
          borderColor: '#2196f3',
          borderWidth: 1,
          borderRadius: 4,
        },
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          labels: { font: { size: 12 }, boxWidth: 12, padding: 12 }
        }
      },
      scales: {
        y: { beginAtZero: true, ticks: { stepSize: 1 } },
        x: { grid: { display: false } }
      }
    }
  })
}

function switchTab(t) {
  tab.value = t
  nextTick(() => buildChart())
}

function formatDate(ts) {
  const d = new Date(ts)
  return `${d.getDate()}.${d.getMonth() + 1} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

function formatDuration(sec) {
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

onMounted(async () => {
  await nextTick()
  buildChart()
})

onUnmounted(() => {
  if (chart) chart.destroy()
})
</script>

<style scoped>
.stats-page {
  max-width: 700px;
  margin: 0 auto;
  text-align: center;
}

h2 {
  color: #2c3e50;
  margin-bottom: 20px;
}

.empty {
  color: #bbb;
  font-size: 16px;
  margin: 40px 0;
}

.tabs {
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-bottom: 16px;
}

.tab {
  padding: 8px 24px;
  border: 2px solid #ddd;
  border-radius: 10px;
  background: white;
  color: #888;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
}

.tab:hover {
  border-color: #aaa;
  color: #555;
}

.tab.active {
  border-color: #42b883;
  background: #42b883;
  color: white;
}

.chart-box {
  height: 220px;
  margin-bottom: 24px;
}

.summary {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.sum-card {
  background: #f5f5f5;
  border-radius: 10px;
  padding: 12px 18px;
  min-width: 80px;
}

.sum-val {
  font-size: 22px;
  font-weight: 900;
  color: #2c3e50;
}

.sum-label {
  font-size: 11px;
  color: #999;
  margin-top: 2px;
}

.session-list {
  text-align: left;
}

.session-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
  font-size: 14px;
}

.session-date {
  color: #888;
  min-width: 90px;
}

.session-result {
  min-width: 60px;
}

.ok {
  color: #4caf50;
  font-weight: 700;
}

.err {
  color: #f44336;
  font-weight: 700;
}

.session-time {
  color: #888;
  min-width: 40px;
}

.session-avg {
  color: #bbb;
  font-size: 12px;
}

.back {
  margin-top: 24px;
}

.back a {
  color: #bbb;
  text-decoration: none;
  font-size: 14px;
}

.back a:hover {
  color: #888;
}
</style>
